import "./styles.css";
import {
  getOfferedUpgrades,
  canGust,
  chooseUpgrade,
  createGame,
  getGustCost,
  getIncomingThreats,
  getRemainingThreats,
  updateGame
} from "./engine/simulation";
import type { GameEvent, GameState, UpgradeId } from "./engine/types";
import { InputController } from "./input/input";
import { clearRun, loadRun, saveRun } from "./platform/persistence";
import { GameAudio } from "./presentation/audio";
import { GameRenderer } from "./presentation/renderer";

function required<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) {
    throw new Error(`Missing required element: ${selector}`);
  }
  return element;
}

const app = required<HTMLElement>("#app");
const canvas = required<HTMLCanvasElement>("#game-canvas");
const startScreen = required<HTMLElement>("#start-screen");
const pauseScreen = required<HTMLElement>("#pause-screen");
const upgradeScreen = required<HTMLElement>("#upgrade-screen");
const endScreen = required<HTMLElement>("#end-screen");
const startButton = required<HTMLButtonElement>("#start-button");
const continueButton = required<HTMLButtonElement>("#continue-button");
const pauseButton = required<HTMLButtonElement>("#pause-button");
const resumeButton = required<HTMLButtonElement>("#resume-button");
const restartButton = required<HTMLButtonElement>("#restart-button");
const againButton = required<HTMLButtonElement>("#again-button");
const gustButton = required<HTMLButtonElement>("#gust-button");
const gustHint = required<HTMLElement>("#gust-hint");
const healthFill = required<HTMLElement>("#health-fill");
const healthValue = required<HTMLElement>("#health-value");
const pressureFill = required<HTMLElement>("#pressure-fill");
const pressureValue = required<HTMLElement>("#pressure-value");
const windDirection = required<HTMLElement>("#wind-direction");
const waveLabel = required<HTMLElement>("#wave-label");
const objectiveValue = required<HTMLElement>("#objective-value");
const incomingValue = required<HTMLElement>("#incoming-value");
const upgradeOptions = required<HTMLElement>("#upgrade-options");
const announcement = required<HTMLElement>("#announcement");
const endEyebrow = required<HTMLElement>("#end-eyebrow");
const endTitle = required<HTMLElement>("#end-title");
const endSummary = required<HTMLElement>("#end-summary");

const renderer = new GameRenderer(canvas);
const input = new InputController(canvas, (x, y) => renderer.screenToWorld(x, y));
const audio = new GameAudio();
let state = createGame(0x4b495445);
let started = false;
let paused = false;
let lastFrame = performance.now();
let accumulator = 0;
let lastPhase = state.phase;
let announcementTimer = 0;
let saveTimer = 0;

const FIXED_STEP = 1 / 60;
const MAX_STEPS = 6;

startButton.addEventListener("click", () => {
  void audio.start();
  startRun();
});

continueButton.addEventListener("click", () => {
  void audio.start();
  continueRun();
});

againButton.addEventListener("click", () => {
  void audio.start();
  startRun();
});

pauseButton.addEventListener("click", pause);
resumeButton.addEventListener("click", resume);
restartButton.addEventListener("click", startRun);

gustButton.addEventListener("pointerdown", (event) => {
  event.preventDefault();
  input.requestGust();
});

window.addEventListener("keydown", (event) => {
  if (event.code === "Escape" && started && state.phase === "running") {
    paused ? resume() : pause();
  }
});

document.addEventListener("visibilitychange", () => {
  if (document.hidden && started && state.phase === "running" && !paused) {
    saveRun(state);
    pause();
  }
});

function startRun(): void {
  clearRun();
  const seed = (Date.now() ^ Math.floor(performance.now() * 1000)) >>> 0;
  state = createGame(seed);
  started = true;
  paused = false;
  accumulator = 0;
  lastPhase = state.phase;
  hideAllScreens();
  app.classList.add("playing");
  showAnnouncement("TAKE THE SHOT");
  audio.receive(state.events);
}

function continueRun(): void {
  const saved = loadRun();
  if (!saved) {
    continueButton.hidden = true;
    startRun();
    return;
  }
  state = saved;
  started = true;
  paused = false;
  accumulator = 0;
  lastPhase = state.phase;
  hideAllScreens();
  app.classList.add("playing");
  if (state.phase === "upgrade") {
    renderUpgradeChoices();
    upgradeScreen.classList.add("active");
  } else {
    showAnnouncement(`WAVE ${state.wave + 1} · JOURNEY RESUMED`);
  }
}

function pause(): void {
  if (!started || state.phase !== "running") {
    return;
  }
  saveRun(state);
  paused = true;
  pauseScreen.classList.add("active");
}

function resume(): void {
  paused = false;
  pauseScreen.classList.remove("active");
  lastFrame = performance.now();
}

function hideAllScreens(): void {
  startScreen.classList.remove("active");
  pauseScreen.classList.remove("active");
  upgradeScreen.classList.remove("active");
  endScreen.classList.remove("active");
}

function processEvents(events: GameEvent[]): void {
  renderer.receive(events, state);
  audio.receive(events);
  for (const event of events) {
    if (event.kind === "wave-clear") {
      showAnnouncement("THE AIR CLEARS");
    } else if (event.kind === "wave-start") {
      showAnnouncement(state.wave === 3 ? "THE RED TEMPEST" : `WAVE ${state.wave + 1}`);
    } else if (event.kind === "wind-set") {
      showAnnouncement("WIND TURNED");
    } else if (event.kind === "curved-hit") {
      showAnnouncement("CURVED HIT");
    } else if (event.kind === "projectile-lost" && state.statistics.misses === 1) {
      showAnnouncement("SHOT LOST TO THE STORM");
    } else if (event.kind === "hearth-hit") {
      showAnnouncement(`BREACH ${state.statistics.breaches} · HEARTH ${Math.ceil(state.hearth.health)}`);
    }
  }
}

function handlePhaseChange(): void {
  if (state.phase === lastPhase) {
    return;
  }
  lastPhase = state.phase;
  if (state.phase === "upgrade") {
    renderUpgradeChoices();
    window.setTimeout(() => upgradeScreen.classList.add("active"), 380);
  } else if (state.phase === "victory" || state.phase === "defeat") {
    showEndScreen();
  }
}

function renderUpgradeChoices(): void {
  upgradeOptions.replaceChildren();
  for (const upgrade of getOfferedUpgrades(state)) {
    const button = document.createElement("button");
    button.className = "upgrade-card";
    button.type = "button";
    button.innerHTML = `
      <span class="family">${upgrade.family}</span>
      <strong>${upgrade.name}</strong>
      <p>${upgrade.description}</p>
    `;
    button.addEventListener("click", () => selectUpgrade(upgrade.id));
    upgradeOptions.append(button);
  }
}

function selectUpgrade(id: UpgradeId): void {
  chooseUpgrade(state, id);
  saveRun(state);
  upgradeScreen.classList.remove("active");
  lastPhase = state.phase;
  processEvents(state.events);
}

function showEndScreen(): void {
  clearRun();
  const won = state.phase === "victory";
  endEyebrow.textContent = won ? "THE HEARTH ENDURES" : "THE STORM BREAKS THROUGH";
  endTitle.textContent = won ? "Storm held" : "Skyfallen";
  const accuracy = state.statistics.shots > 0
    ? Math.round(state.statistics.hits / state.statistics.shots * 100)
    : 0;
  const curvedRate = state.statistics.hits > 0
    ? Math.round(state.statistics.curvedHits / state.statistics.hits * 100)
    : 0;
  endSummary.textContent = `${state.statistics.defeats} raiders turned • ${state.statistics.breaches} breaches • ${accuracy}% contact • ${curvedRate}% curved`;
  window.setTimeout(() => endScreen.classList.add("active"), 500);
}

function showAnnouncement(text: string): void {
  window.clearTimeout(announcementTimer);
  announcement.textContent = text;
  announcement.classList.add("visible");
  announcementTimer = window.setTimeout(() => announcement.classList.remove("visible"), 1200);
}

function updateHud(): void {
  const healthRatio = Math.max(0, state.hearth.health / state.hearth.maxHealth);
  const pressureRatio = Math.max(0, state.pressure / state.modifiers.pressureCapacity);
  healthFill.style.width = `${healthRatio * 100}%`;
  healthValue.textContent = Math.ceil(state.hearth.health).toString();
  pressureFill.style.width = `${pressureRatio * 100}%`;
  pressureValue.textContent = Math.floor(state.pressure).toString();
  windDirection.textContent = `WIND ${cardinalDirection(state.wind.targetAngle)}`;
  waveLabel.textContent = state.wave === 3 ? "FINAL • TEMPEST" : `WAVE ${state.wave + 1} OF 4`;
  objectiveValue.textContent = getRemainingThreats(state).toString();
  const incoming = getIncomingThreats(state);
  const ready = state.towers.filter((tower) => tower.cooldown <= 0).length;
  const threats = incoming > 0 ? `${incoming} INCOMING` : `${state.enemies.length} ACTIVE`;
  incomingValue.textContent = `${ready} READY · ${threats}`;
  gustHint.textContent = `${Math.ceil(getGustCost(state))} PRESSURE · SPACE`;
  gustButton.disabled = !canGust(state) || paused;
}

function cardinalDirection(angle: number): string {
  const directions = ["EAST", "SOUTHEAST", "SOUTH", "SOUTHWEST", "WEST", "NORTHWEST", "NORTH", "NORTHEAST"];
  const normalized = (angle + Math.PI * 2) % (Math.PI * 2);
  return directions[Math.round(normalized / (Math.PI / 4)) % directions.length] ?? "EAST";
}

function frame(now: number): void {
  const elapsed = Math.min(0.1, (now - lastFrame) / 1000);
  lastFrame = now;

  if (started && !paused && state.phase === "running") {
    accumulator += elapsed;
    saveTimer += elapsed;
    let steps = 0;
    while (accumulator >= FIXED_STEP && steps < MAX_STEPS) {
      updateGame(state, input.poll(), FIXED_STEP);
      processEvents(state.events);
      accumulator -= FIXED_STEP;
      steps += 1;
    }
    if (steps === MAX_STEPS) {
      accumulator = 0;
    }
    handlePhaseChange();
    if (saveTimer >= 2) {
      saveRun(state);
      saveTimer = 0;
    }
  } else {
    input.poll();
    accumulator = 0;
  }

  updateHud();
  audio.update(state);
  renderer.render(state, elapsed);
  requestAnimationFrame(frame);
}

requestAnimationFrame(frame);

continueButton.hidden = loadRun() === null;
